import { useState } from 'react';
import { Eye, ShieldAlert, LogOut, Users } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';

/**
 * Persistent, full-width banner shown whenever a session is being driven by
 * someone other than the account owner:
 *  - admin read  → amber "Viewing as X — read only"
 *  - admin full  → red "Impersonating X — changes are live & audited"
 *  - parent      → primary "You're in X's workspace" (parent acting for a login-less child)
 * Mounted globally (App.js) so it appears across every shell/route.
 */
export const ImpersonationBanner = () => {
  const { impersonation, exitImpersonation } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!impersonation) return null;
  const isParent = impersonation.kind === 'parent';
  const full = impersonation.mode === 'full';

  const tone = isParent
    ? 'bg-primary text-primary-foreground'
    : full ? 'bg-destructive text-white' : 'bg-gold text-gold-foreground';
  const exitBtn = isParent
    ? 'bg-black/15 hover:bg-black/25 text-primary-foreground'
    : full ? 'bg-white/25 hover:bg-white/40 text-white' : 'bg-black/15 hover:bg-black/25 text-gold-foreground';
  const label = isParent
    ? `You're in ${impersonation.target_name}'s workspace`
    : full
      ? `Impersonating ${impersonation.target_name} — changes are live & audited`
      : `Viewing as ${impersonation.target_name} — read only`;
  const Icon = isParent ? Users : full ? ShieldAlert : Eye;

  const exit = async () => {
    setBusy(true);
    try {
      await exitImpersonation();
      window.location.href = isParent ? '/app/parent' : '/app/admin/users';
    } catch {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className={cn('fixed top-0 inset-x-0 z-[70] h-10 flex items-center justify-center gap-3 px-4 text-sm font-semibold shadow-md', tone)}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
      <button
        type="button"
        data-testid="exit-impersonation-btn"
        onClick={exit}
        disabled={busy}
        className={cn('ml-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors disabled:opacity-60', exitBtn)}
      >
        <LogOut className="h-3.5 w-3.5" /> {busy ? 'Exiting…' : (isParent ? 'Back to parent' : 'Exit')}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
